import React, { useState } from "react";
import { Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Popover, Select, TextField, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { activeFilterKeys, clearAllFilters, getFilterChips, setFilterValue, toggleFilter } from "./filterModel";

const ACCENT = "#26890d";
const inputSx = { backgroundColor: "#fff", "& .MuiInputBase-root": { fontSize: "0.85rem" } };

// "Filters" button + panel: one labelled row per filter, a checkbox to switch it
// on, and its input underneath. All switched-on filters apply together and take
// effect as you change them. What is applied shows as chips (<FilterChips>).
export const FilterPanel = ({ fields, filters, onFiltersChange, height = 37 }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const count = activeFilterKeys(filters).length;
  const setValue = (key) => (value) => onFiltersChange(setFilterValue(filters, key, value));

  const control = (field) => {
    if (field.type === "date") {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disableFuture
            value={filters[field.key].value || null}
            onChange={setValue(field.key)}
            renderInput={(params) => (
              <TextField {...params} size="small" fullWidth sx={inputSx} inputProps={{ ...params.inputProps, placeholder: field.placeholder ?? "Select date", "aria-label": field.label }} />
            )}
          />
        </LocalizationProvider>
      );
    }
    if (field.type === "select") {
      // A short fixed list (e.g. Yes / No) — a plain dropdown rather than a searchable one.
      const options = field.options ?? [];
      return (
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={filters[field.key].value || ""}
          onChange={(e) => setValue(field.key)(e.target.value)}
          renderValue={(value) => options.find((o) => o.value === value)?.label ?? <span style={{ color: "#999" }}>{field.placeholder ?? `Select ${field.label.toLowerCase()}`}</span>}
          inputProps={{ "aria-label": field.label }}
          sx={{ ...inputSx, fontSize: "0.85rem" }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.85rem" }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    }
    if (field.type === "autocomplete") {
      const options = field.options ?? [];
      return (
        <Autocomplete
          size="small"
          options={options}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={options.find((o) => o.value === filters[field.key].value) ?? null}
          onChange={(_, option) => setValue(field.key)(option?.value ?? "")}
          renderInput={(params) => (
            <TextField {...params} placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}`} inputProps={{ ...params.inputProps, "aria-label": field.label }} />
          )}
          sx={inputSx}
        />
      );
    }
    return (
      <TextField
        size="small"
        fullWidth
        placeholder={field.placeholder ?? `Search by ${field.label.toLowerCase()}`}
        value={filters[field.key].value}
        onChange={(e) => setValue(field.key)(e.target.value)}
        inputProps={{ "aria-label": field.label }}
        sx={inputSx}
      />
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<FilterListIcon />}
        aria-haspopup="dialog"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          height,
          color: "#333",
          borderColor: count ? ACCENT : "#c4c4c4",
          backgroundColor: "#fff",
          "&:hover": { borderColor: ACCENT, backgroundColor: "#fff" },
        }}
      >
        Filters
        {count > 0 && (
          <Box
            component="span"
            aria-label={`${count} applied`}
            sx={{ ml: 1, minWidth: 20, height: 20, px: 0.6, borderRadius: "10px", backgroundColor: ACCENT, color: "#fff", fontSize: "0.72rem", lineHeight: "20px", textAlign: "center" }}
          >
            {count}
          </Box>
        )}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, p: 2, mt: 0.5 }, role: "dialog", "aria-label": "Filters" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>Filters</Typography>
          <Button size="small" disabled={count === 0} onClick={() => onFiltersChange(clearAllFilters(filters, fields))} sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Clear all
          </Button>
        </Box>
        {fields.map((field) => (
          <Box key={field.key} sx={{ py: 0.25 }}>
            <FormControlLabel
              sx={{ mx: 0, "& .MuiFormControlLabel-label": { fontSize: "0.88rem", fontWeight: 600 } }}
              control={<Checkbox size="small" checked={filters[field.key].on} onChange={() => onFiltersChange(toggleFilter(filters, field.key, fields))} />}
              label={field.label}
            />
            {filters[field.key].on && <Box sx={{ ml: 3.5, mb: 1 }}>{control(field)}</Box>}
          </Box>
        ))}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button size="small" variant="contained" disableElevation onClick={() => setAnchorEl(null)} sx={{ textTransform: "none", backgroundColor: ACCENT, "&:hover": { backgroundColor: "#1f6e0a" } }}>
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

// What is applied, one removable chip each, plus Clear all. Renders nothing when no filter has a value.
export const FilterChips = ({ fields, filters, onFiltersChange }) => {
  const chips = getFilterChips(filters, fields);
  if (chips.length === 0) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 1 }} aria-label="Applied filters">
      {chips.map(({ key, label, text }) => (
        <Chip
          key={key}
          size="small"
          label={
            <>
              <span style={{ color: "#777" }}>{label}: </span>
              <strong>{text}</strong>
            </>
          }
          onDelete={() => onFiltersChange(toggleFilter(filters, key, fields))}
          sx={{ backgroundColor: "rgba(38,137,13,0.08)", border: "1px solid rgba(38,137,13,0.25)", fontSize: "0.78rem" }}
        />
      ))}
      {chips.length > 1 && (
        <Button size="small" onClick={() => onFiltersChange(clearAllFilters(filters, fields))} sx={{ textTransform: "none", fontSize: "0.78rem", minWidth: 0 }}>
          Clear all
        </Button>
      )}
    </Box>
  );
};
